import React, { useState } from 'react';
import { Check, Trophy, BookOpen, Sparkles, ArrowRight, Lock, Zap, Shield, CreditCard } from 'lucide-react';
interface Plan {
  id: 'standard' | 'complete';
  name: string;
  icon: React.ReactNode;
  description: string;
  oldPrice: string;
  price: string;
  badges?: {
    label: string;
    type: 'best' | 'save';
  }[];
  highlight?: boolean;
}
const plans: Plan[] = [{
  id: 'standard',
  name: 'CALISTENIA AVANZADA',
  icon: <BookOpen className="w-4 h-4 text-blue-400" data-id="element-390" />,
  description: '6 módulos + 6 bonos estratégicos',
  oldPrice: '$34.990',
  price: '$19.990'
}, {
  id: 'complete',
  name: 'PACK COMPLETO',
  icon: <Trophy className="w-4 h-4 text-brand-orange" data-id="element-391" />,
  description: '6 módulos + 6 bonos + Pro Élite™',
  oldPrice: '$43.990',
  price: '$27.990',
  badges: [{
    label: '⭐ MÁS ELEGIDO',
    type: 'best'
  }, {
    label: 'AHORRÁS $16.000',
    type: 'save'
  }],
  highlight: true
}];
interface PricingChoiceProps {
  showTrustStrip?: boolean;
  defaultSelected?: 'standard' | 'complete';
}
export function PricingChoice({
  showTrustStrip = true,
  defaultSelected = 'complete'
}: PricingChoiceProps) {
  const [selected, setSelected] = useState<'standard' | 'complete'>(defaultSelected);
  const selectedPlan = plans.find(p => p.id === selected)!;
  return <div className="max-w-2xl mx-auto" data-id="element-392">
      <div className="space-y-3" data-id="element-393">
        {plans.map(plan => {
        const isActive = selected === plan.id;
        return <button key={plan.id} onClick={() => setSelected(plan.id)} className={`w-full text-left rounded-xl border-2 transition-all duration-300 p-3 md:p-4 relative ${isActive ? 'border-brand-orange bg-brand-dark shadow-[0_0_25px_rgba(255,107,26,0.2)]' : 'border-brand-border bg-brand-card hover:border-brand-orange/40'}`} aria-pressed={isActive} data-id="element-394">
              <div className="flex items-center gap-3" data-id="element-395">
                {/* Radio */}
                <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isActive ? 'border-brand-orange' : 'border-brand-muted'}`} data-id="element-396">
                  {isActive && <div className="w-2.5 h-2.5 rounded-full bg-brand-orange" data-id="element-397" />}
                </div>

                {/* Middle content */}
                <div className="flex-grow min-w-0" data-id="element-398">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5" data-id="element-399">
                    {plan.icon}
                    <h4 className="font-bold text-sm md:text-base tracking-wide" data-id="element-400">
                      {plan.name}
                    </h4>
                  </div>
                  {plan.badges && isActive && <div className="flex flex-wrap gap-1.5 my-1.5" data-id="element-401">
                      {plan.badges.map((b, i) => <span key={i} className={`text-[9px] md:text-[10px] font-bold px-1.5 py-0.5 rounded ${b.type === 'best' ? 'bg-brand-orange text-white' : 'bg-green-500/20 text-green-400 border border-green-500/30'}`} data-id="element-402">
                          {b.label}
                        </span>)}
                    </div>}
                  <p className="text-[11px] md:text-xs text-brand-muted leading-snug" data-id="element-403">
                    {plan.description}
                  </p>
                </div>

                {/* Price */}
                <div className="text-right shrink-0" data-id="element-404">
                  <p className="text-[10px] md:text-xs text-brand-muted line-through leading-none mb-0.5" data-id="element-405">
                    {plan.oldPrice}
                  </p>
                  <p className={`font-display text-xl md:text-2xl leading-none ${isActive ? 'text-brand-orange' : 'text-white'}`} data-id="element-406">
                    {plan.price}
                  </p>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Shared CTA */}
      <button className="mt-4 w-full bg-brand-orange hover:bg-brand-orangeHover text-white font-display text-lg md:text-2xl tracking-wider py-4 md:py-5 rounded-xl transition-all duration-300 shadow-[0_0_30px_rgba(255,107,26,0.3)] hover:shadow-[0_0_40px_rgba(255,107,26,0.5)] flex items-center justify-center gap-2 group" onClick={() => {
      // could integrate checkout per selectedPlan.id
    }} data-id="element-407">
        SÍ, QUIERO{' '}
        {selectedPlan.id === 'complete' ? 'PACK COMPLETO' : 'CALISTENIA AVANZADA'}
        <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" data-id="element-408" />
      </button>

      <p className="text-center text-[11px] md:text-xs text-brand-muted mt-3 italic" data-id="element-409">
        {selectedPlan.id === 'complete' ? 'La opción más elegida por atletas que buscan el máximo rendimiento' : 'Acceso inmediato al sistema completo + 6 bonos'}
      </p>

      {/* Trust strip */}
      {showTrustStrip && <div className="mt-4 bg-brand-card border border-brand-border rounded-xl px-3 md:px-4 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] md:text-xs text-brand-muted" data-id="element-410">
          <span className="flex items-center gap-1.5" data-id="element-411">
            <Lock className="w-3.5 h-3.5 text-brand-orange" data-id="element-412" /> Pago seguro
          </span>
          <span className="flex items-center gap-1.5" data-id="element-413">
            <Zap className="w-3.5 h-3.5 text-brand-orange" data-id="element-414" /> Acceso en 5 min
          </span>
          <span className="flex items-center gap-1.5" data-id="element-415">
            <Shield className="w-3.5 h-3.5 text-brand-orange" data-id="element-416" /> Garantía 7 días
          </span>
          <span className="flex items-center gap-1.5" data-id="element-417">
            <CreditCard className="w-3.5 h-3.5 text-brand-orange" data-id="element-418" /> Mercado
            Pago
          </span>
        </div>}
    </div>;
}